import React, { useCallback, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import {
  applyTheme,
  getStoredTheme,
  setTheme,
  THEME_STORAGE_KEY,
} from '../utils/theme';
import { cn } from '../lib/utils';

function getInitialDark(): boolean {
  const stored = getStoredTheme();
  if (stored) return stored === 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeToggle() {
  const [dark, setDark] = useState(getInitialDark);

  useEffect(() => {
    const syncFromStorage = () => {
      const stored = getStoredTheme();
      if (stored) {
        applyTheme(stored);
        setDark(stored === 'dark');
      }
    };

    syncFromStorage();

    const onStorage = (e: StorageEvent) => {
      if (
        e.key === THEME_STORAGE_KEY &&
        (e.newValue === 'dark' || e.newValue === 'light')
      ) {
        applyTheme(e.newValue);
        setDark(e.newValue === 'dark');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleColorMode = useCallback(() => {
    setDark((prev) => {
      const next = prev ? 'light' : 'dark';
      setTheme(next);
      return !prev;
    });
  }, []);

  return (
    <div className="flex items-center gap-2">
      <Sun
        className={cn(
          'h-4 w-4 shrink-0',
          dark ? 'text-muted-foreground' : 'text-foreground'
        )}
        aria-hidden
      />
      <button
        type="button"
        role="switch"
        aria-checked={dark}
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        onClick={toggleColorMode}
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border p-1 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          dark
            ? 'border-primary bg-primary'
            : 'border-border bg-muted'
        )}
      >
        <span
          className={cn(
            'pointer-events-none block h-5 w-5 rounded-full bg-background shadow ring-1 ring-black/5 transition-transform duration-200 ease-out',
            dark ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
      <Moon
        className={cn(
          'h-4 w-4 shrink-0',
          dark ? 'text-foreground' : 'text-muted-foreground'
        )}
        aria-hidden
      />
    </div>
  );
}
